import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Shield, CheckCircle, Lock, FileText, Loader2, AlertCircle, Edit3, Type } from 'lucide-react';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

const STEPS = { LOADING: 'loading', VIEWING: 'viewing', SIGNING: 'signing', DONE: 'done', ERROR: 'error' };

export default function SigningPage() {
  const { token } = useParams();
  const [step, setStep] = useState(STEPS.LOADING);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [sigMode, setSigMode] = useState('draw'); // draw | type
  const [typedSig, setTypedSig] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    api.get(`/sign/${token}`)
      .then(({ data }) => {
        if (data.already_signed) { setStep(STEPS.DONE); return; }
        setSession(data);
        setFullName(data.recipient?.name || '');
        setEmail(data.recipient?.email || '');
        setStep(STEPS.VIEWING);
      })
      .catch(err => {
        setError(err.response?.data?.error || 'This link is invalid or expired');
        setStep(STEPS.ERROR);
      });
  }, [token]);

  const markViewed = async () => {
    try { await api.post(`/sign/${token}/view`); } catch (_) {}
    setStep(STEPS.SIGNING);
  };

  const clearSig = () => { if (sigRef.current) sigRef.current.clear(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return alert('Please agree to the terms before signing');
    if (!fullName || !email) return alert('Full name and email are required');

    let sigData = '';
    if (sigMode === 'draw') {
      if (!sigRef.current || sigRef.current.isEmpty()) return alert('Please draw your signature');
      sigData = sigRef.current.toDataURL('image/png');
    } else {
      if (!typedSig.trim()) return alert('Please type your signature');
      sigData = typedSig;
    }

    // Build signatures array — one per signer field
    const signatures = (session.fields || [])
      .filter(f => f.field_type === 'signature' || f.field_type === 'initials')
      .map(f => ({ field_id: f.id, type: sigMode, data: sigData }));

    // If no fields defined, create a virtual signature
    if (signatures.length === 0) {
      signatures.push({ field_id: null, type: sigMode, data: sigData });
    }

    setSubmitting(true);
    try {
      await api.post(`/sign/${token}/submit`, { signatures, full_name: fullName, email });
      setStep(STEPS.DONE);
    } catch (err) {
      alert(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = session?.company?.primary_color || '#1a56db';

  // ── Loading ────────────────────────────────────────────────
  if (step === STEPS.LOADING) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-blue-600" />
    </div>
  );

  // ── Error ─────────────────────────────────────────────────
  if (step === STEPS.ERROR) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-sm w-full text-center">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-800">Link Unavailable</h2>
        <p className="text-slate-500 mt-2 text-sm">{error}</p>
      </div>
    </div>
  );

  // ── Done ─────────────────────────────────────────────────
  if (step === STEPS.DONE) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-sm w-full text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={36} className="text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">All Done!</h2>
        <p className="text-slate-500 mt-2">Your signature has been captured successfully. A copy will be emailed to you once all parties sign.</p>
        <div className="mt-6 bg-slate-100 rounded-xl p-4 text-xs text-slate-500">
          <Lock size={12} className="inline mr-1" />
          Secured by PontoonSign · pontoonglobal.com
        </div>
      </div>
    </div>
  );

  // ── Viewing (intro screen) ────────────────────────────────
  if (step === STEPS.VIEWING) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Company brand */}
        <div className="text-center mb-6">
          {session.company?.logo_url && (
            <img src={session.company.logo_url} alt="Company logo" className="h-10 mx-auto mb-3 object-contain" />
          )}
          <div className="text-sm font-semibold text-slate-700">{session.company?.name}</div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-800 text-sm">{session.document?.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Requested by {session.sender?.name}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600">
              <p>Hello <strong>{session.recipient?.name}</strong>,</p>
              <p className="mt-2">{session.document?.message || 'Please review and sign the document below.'}</p>
            </div>

            {/* Trust signals */}
            <div className="space-y-2">
              {[
                { icon: Shield, text: `This document was sent to ${session.recipient?.email}` },
                { icon: Lock, text: 'Secure, encrypted signing link' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-xs text-slate-500">
                  <Icon size={13} className="text-blue-400 flex-shrink-0" />
                  <span>{text}</span>
                </div>
              ))}
            </div>

            {session.document?.expires_at && (
              <div className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                ⏰ Expires {new Date(session.document.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>

          <div className="px-6 pb-6">
            <button
              onClick={markViewed}
              className="w-full text-white font-semibold py-3.5 rounded-xl text-sm transition-all active:scale-[0.99]"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, #0e3fa8)` }}
            >
              Review & Sign Document →
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Sent via pontoonglobal.com · Secure Document Platform
        </p>
      </div>
    </div>
  );

  // ── Signing ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Lock size={14} className="text-blue-600" />
          <span className="text-xs font-semibold text-slate-700">Secure Signing</span>
        </div>
        <div className="text-xs text-slate-500">{session.company?.name}</div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 space-y-5">
        {/* Document info */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Signing</div>
          <div className="font-bold text-slate-800 text-lg leading-tight">{session.document?.title}</div>
          <div className="text-xs text-slate-500 mt-1">Requested by {session.sender?.name} · {session.company?.name}</div>
        </div>

        {/* Signer info */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-700">Your Information</div>
          <div className="grid grid-cols-1 gap-3">
            <input
              placeholder="Full Legal Name *"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
            <input
              type="email"
              placeholder="Email Address *"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
        </div>

        {/* Signature capture */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-700">Your Signature</div>
            <div className="flex rounded-lg overflow-hidden border border-slate-200">
              <button
                onClick={() => setSigMode('draw')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  sigMode === 'draw' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Edit3 size={12} /> Draw
              </button>
              <button
                onClick={() => setSigMode('type')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  sigMode === 'type' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Type size={12} /> Type
              </button>
            </div>
          </div>

          {sigMode === 'draw' ? (
            <>
              <div className="border-2 border-dashed border-slate-200 rounded-xl overflow-hidden bg-slate-50 touch-none">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{ className: 'w-full', height: 140 }}
                  backgroundColor="transparent"
                  penColor="#1e3a8a"
                />
              </div>
              <button
                onClick={clearSig}
                className="mt-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Clear and redraw
              </button>
            </>
          ) : (
            <div>
              <input
                placeholder="Type your full name as signature"
                value={typedSig}
                onChange={e => setTypedSig(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-4 text-2xl font-serif text-blue-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all italic"
                style={{ fontFamily: "'Georgia', serif" }}
              />
              <div className="text-xs text-slate-400 mt-2">
                This typed text represents your legal signature
              </div>
            </div>
          )}
        </div>

        {/* Agreement */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-600"
            />
            <span className="text-xs text-slate-500 leading-relaxed">
              I agree that my signature on this document is legally binding. I understand that by clicking "Sign Document", I am signing this document electronically and agree that the signature shall have the same legal force and effect as a handwritten signature.
            </span>
          </label>
        </div>

        {/* Submit */}
        <form onSubmit={handleSubmit}>
          <button
            type="submit"
            disabled={submitting || !agreed}
            className="w-full text-white font-bold py-4 rounded-2xl text-base transition-all disabled:opacity-50 active:scale-[0.99] flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${primaryColor}, #0e3fa8)` }}
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
            {submitting ? 'Submitting...' : 'Sign Document'}
          </button>
        </form>

        <div className="text-center text-xs text-slate-400 pb-4">
          <Lock size={11} className="inline mr-1" />
          Your signature is captured with timestamp and IP address for audit purposes.
          <br />Secured by pontoonglobal.com
        </div>
      </div>
    </div>
  );
}
